<template>
  <div class="fpf" ref="wrapper">
    <!-- Each branch root is keyed so Vue never patches one branch's DOM node into
         another. Without keys the unkeyed diff reuses a `div` for whatever branch comes
         next; when the previous branch held the body-portaled dropdown, the clear button
         inherited that moved node and vanished from the DOM (ER-12679). -->
    <button
      v-if="!value && !isOpen"
      key="fpf-trigger"
      type="button"
      class="fpf__trigger"
      :aria-label="selectPlaceholder"
      :aria-expanded="false"
      :disabled="disabled"
      @click.stop="openSearch"
    >
      <span class="fpf__trigger-placeholder">{{ selectPlaceholder }}</span>
      <Icon name="chevron_down" class="fpf__chevron" />
    </button>

    <div v-else-if="!value || isOpen" key="fpf-search" class="fpf__search-wrapper">
      <div ref="searchField" class="fpf__search-field">
        <span v-if="searchLabel" class="fpf__search-label">{{ searchLabel }}</span>
        <ExTextInput
          ref="searchInput"
          :value="query"
          :ghostText="resolvedSearchPlaceholder"
          icon="interface_search"
          :hideFloatingLabel="true"
          :disabled="disabled"
          @input="onInput"
          @focus="keepOpen"
        />
        <button
          type="button"
          class="fpf__chevron-btn"
          aria-label="Close"
          :disabled="disabled"
          @click.stop="closeSearch"
        >
          <Icon name="chevron_down" class="fpf__chevron fpf__chevron--open" />
        </button>
      </div>

      <div
        v-if="showDropdown"
        key="fpf-dropdown"
        ref="dropdownEl"
        class="fpf__dropdown"
        :style="dropdownStyle"
        role="listbox"
      >
        <FlagPickerResultsList
          :items="filteredItems"
          :loading="isTyping || loading"
          :has-query="Boolean(query)"
          :empty-message="emptyMessage"
          :format-label="formatLabel"
          @select="selectItem"
        />
      </div>
    </div>

    <div v-else key="fpf-picked" class="fpf__picked" :class="{ 'fpf__picked--disabled': disabled }">
      <img
        v-if="pickedFlagVisible"
        :src="pickedFlagSrc"
        :alt="''"
        class="fpf__picked-flag"
        @error="handlePickedFlagError"
      />
      <span class="fpf__picked-label">{{ formatLabel(value) }}</span>
      <Icon
        v-if="disabled"
        name="lock_icon"
        class="fpf__lock-icon"
        aria-hidden="true"
      />
      <div
        v-else
        class="fpf__clear"
        role="button"
        tabindex="0"
        aria-label="Clear selection"
        @click.stop="clearSelection"
        @keydown.enter.prevent="clearSelection"
        @keydown.space.prevent="clearSelection"
      >
        <!-- Inline glyph: the picker must always show its clear affordance, even before
             the lazily-loaded Icon component has resolved. -->
        <svg
          class="fpf__clear-icon"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M9 3L3 9M3 3L9 9"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from "vue";
import { useBodyPortalDropdown } from "@components/utils/useBodyPortalDropdown";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import Icon from "@components/icons/Icon.vue";
import FlagPickerResultsList from "./FlagPickerResultsList.vue";
import {
  resolveCountryFlagIcon,
  resolveCountryFlagFallback,
} from "@components/utils/countryFlagIcon";

const props = defineProps({
  value: { type: Object, default: null },
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  selectPlaceholder: { type: String, default: "Select..." },
  searchPlaceholder: { type: String, default: "Type to search." },
  searchLabel: { type: String, default: "" },
  emptyMessage: { type: String, default: "No options available" },
  formatLabel: {
    type: Function,
    default: (item) => item?.name || "",
  },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(["input", "search", "clear"]);

const pickedFlagUrl = computed(() =>
  props.value ? resolveCountryFlagIcon(props.value) : ""
);

// ER-11925 — a resolved API flag URL can still 502/404 (static storage is not
// reachable in staging/prod). On <img> error, swap to the flagcdn fallback; when
// no ISO-2 code exists for the fallback, hide the image instead of a broken icon.
const pickedFlagFailed = ref(false);
const pickedFlagFallback = computed(() =>
  props.value ? resolveCountryFlagFallback(props.value) : ""
);
const pickedFlagSrc = computed(() =>
  pickedFlagFailed.value ? pickedFlagFallback.value : pickedFlagUrl.value
);
const pickedFlagVisible = computed(() => {
  if (!pickedFlagUrl.value) return false;
  if (!pickedFlagFailed.value) return true;
  return Boolean(pickedFlagFallback.value);
});

function handlePickedFlagError() {
  if (pickedFlagFailed.value) return;
  pickedFlagFailed.value = true;
}

watch(
  () => props.value,
  () => {
    pickedFlagFailed.value = false;
  }
);

const query = ref("");
const isTyping = ref(false);
const isOpen = ref(false);
const wrapper = ref(null);
const searchInput = ref(null);
const debounceTimer = ref(null);
const searchField = ref(null);
const dropdownEl = ref(null);

const resolvedSearchPlaceholder = computed(
  () => props.searchPlaceholder || "Type to search."
);

const showDropdown = computed(() => isOpen.value);

const { dropdownStyle, attach, detach } = useBodyPortalDropdown({
  getAnchor: () => searchField.value,
  getDropdown: () => dropdownEl.value,
  getContainer: () => wrapper.value,
  onClose: () => closeSearch(),
});

watch(showDropdown, (open) => {
  if (open) attach();
  else detach();
});

/** Client-side filter for static country lists (Pay Range global catalog). */
const filteredItems = computed(() => {
  const list = props.items || [];
  const q = (query.value || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter((item) => {
    const name = String(item?.name || "").toLowerCase();
    const code = String(item?.code || "").toLowerCase();
    return name.includes(q) || code.includes(q);
  });
});

function keepOpen() {
  isOpen.value = true;
}

function focusSearchInput() {
  nextTick(() => {
    const input = searchInput.value;
    if (input && typeof input.focus === "function") {
      input.focus();
      return;
    }
    const el = wrapper.value?.querySelector("input");
    if (el && typeof el.focus === "function") el.focus();
  });
}

function openSearch() {
  isOpen.value = true;
  emit("search", query.value);
  focusSearchInput();
}

function closeSearch() {
  query.value = "";
  isTyping.value = false;
  isOpen.value = false;
}

function onInput(value) {
  query.value = value;
  isOpen.value = true;
  isTyping.value = true;
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => {
    isTyping.value = false;
    emit("search", query.value);
  }, 300);
}

function selectItem(item) {
  query.value = "";
  isTyping.value = false;
  isOpen.value = false;
  emit("input", item);
}

function clearSelection() {
  if (props.disabled) return;
  emit("input", null);
  emit("clear");
  emit("search", "");
}

onUnmounted(() => {
  clearTimeout(debounceTimer.value);
});
</script>

<style scoped>
.fpf {
  width: 100%;
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fpf__trigger {
  appearance: none;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.fpf__trigger:hover {
  border-color: #98a2b3;
}

.fpf__trigger:focus-visible {
  outline: none;
  border-color: #d6bbfb;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.05),
    0 0 0 4px rgba(158, 119, 237, 0.24);
}

.fpf__trigger-placeholder {
  flex: 1;
  min-width: 0;
  color: #667085;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.fpf__chevron {
  flex-shrink: 0;
  display: inline-flex;
  width: 20px;
  height: 20px;
  color: #667085;
}

.fpf__chevron-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fpf__chevron--open {
  transform: rotate(180deg);
}

.fpf__search-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fpf__search-field {
  position: relative;
  width: 100%;
}

.fpf__search-label {
  position: absolute;
  left: 10px;
  top: -7px;
  z-index: 2;
  padding: 0 4px;
  background: #fff;
  color: #666;
  font-size: 8px;
  font-weight: 400;
  line-height: 14px;
  pointer-events: none;
  white-space: nowrap;
}

.fpf__search-field ::v-deep .ex_text_input .v-input__slot {
  padding-right: 36px !important;
}

.fpf__dropdown {
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow:
    0 4px 6px -2px rgba(16, 24, 40, 0.03),
    0 12px 16px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.fpf__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

/* ER-11872 / ER-11954 — locked operating country matches CurrencyPicker locked (#e0e0e0). */
.fpf__picked--disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  box-shadow: none;
}

.fpf__picked--disabled .fpf__picked-label {
  color: #4d4d4d;
}

.fpf__lock-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #4d4d4d;
}

.fpf__picked-flag {
  flex-shrink: 0;
  width: 24px;
  height: 18px;
  border-radius: 2px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

.fpf__picked-label {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fpf__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  color: #667085;
  border-radius: 4px;
}

.fpf__clear:hover {
  color: #344054;
}

.fpf__clear-icon {
  flex-shrink: 0;
  display: block;
}

/* XClose.vue hard-codes stroke #98A2B3, so the colour above never reached the glyph.
   Kept for any host that still renders the icon through <Icon>. */
.fpf__clear ::v-deep path {
  stroke: currentColor;
}

.fpf__clear:focus-visible {
  outline: 2px solid #9e77ed;
  outline-offset: 2px;
}
</style>
